import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Users, Search, Plus, User } from "lucide-react";
import { useJugadores } from "../hooks/useJugador";
import Layout from "../components/Layout";
import { useAuth } from "../context/AuthContext";

const POS_SHORT: Record<string,string> = {
  portero:"PO", central:"DC", lateral_derecho:"LD", lateral_izquierdo:"LI",
  pivote:"MCD", interior:"MC", extremo_derecho:"ED", extremo_izquierdo:"EI",
  mediapunta:"MP", delantero_centro:"DC"
};

const ESTADO_COLORS: Record<string,{bg:string,text:string}> = {
  activo:      { bg:"bg-green-50",  text:"text-green-700" },
  inactivo:    { bg:"bg-gray-100",  text:"text-gray-600"  },
  lesionado:   { bg:"bg-red-50",    text:"text-red-700"   },
  prestado:    { bg:"bg-amber-50",  text:"text-amber-700" },
  transferido: { bg:"bg-purple-50", text:"text-purple-700"},
};

export default function JugadoresPage() {
  const nav = useNavigate();
  const { user, isReadOnly } = useAuth();
  const [filtroCategoria, setFiltroCategoria] = useState("");
  const [busqueda, setBusqueda] = useState("");
  const { data: jugadores = [], isLoading } = useJugadores(undefined, filtroCategoria || undefined);

  const filtrados = jugadores.filter((j: any) =>
    busqueda === "" ||
    `${j.nombre} ${j.apellido}`.toLowerCase().includes(busqueda.toLowerCase())
  );

  return (
    <Layout>
      <div className="p-6 md:p-8 max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Users size={20} className="text-blue-500"/>
              <h1 className="text-2xl font-bold text-gray-900">Jugadores</h1>
            </div>
            <p className="text-gray-500 text-sm">{filtrados.length} {filtrados.length === 1 ? "jugador" : "jugadores"}</p>
          </div>
          {!isReadOnly() && (
            <button onClick={() => nav("/jugadores/nuevo")}
              className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2.5 rounded-lg text-sm font-medium transition-colors shadow-sm">
              <Plus size={16}/> Nuevo jugador
            </button>
          )}
        </div>

        {/* Filtros */}
        <div className="flex flex-wrap gap-3 mb-6">
          <div className="relative flex-1 min-w-[200px] max-w-sm">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"/>
            <input
              value={busqueda}
              onChange={e => setBusqueda(e.target.value)}
              placeholder="Buscar jugador..."
              className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
            />
          </div>
          <div className="flex gap-2 flex-wrap">
            {["", "U9","U11","U13","U15","U17"].map(cat => (
              <button key={cat}
                onClick={() => setFiltroCategoria(cat)}
                className={`px-3.5 py-2 rounded-lg text-sm font-medium border transition-colors ${
                  filtroCategoria === cat
                    ? "bg-blue-600 text-white border-blue-600"
                    : "bg-white text-gray-600 border-gray-300 hover:border-gray-400"
                }`}
              >
                {cat || "Todas"}
              </button>
            ))}
          </div>
        </div>

        {/* Grid */}
        {isLoading ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
            {[1,2,3,4,5].map(i => (
              <div key={i} className="bg-white rounded-xl border border-gray-200 p-5 animate-pulse">
                <div className="w-12 h-12 rounded-full bg-gray-200 mx-auto mb-3"/>
                <div className="h-3 bg-gray-200 rounded w-3/4 mx-auto mb-2"/>
                <div className="h-2.5 bg-gray-100 rounded w-1/2 mx-auto"/>
              </div>
            ))}
          </div>
        ) : filtrados.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-xl border border-dashed border-gray-300">
            <User size={40} className="text-gray-300 mx-auto mb-3"/>
            <p className="text-gray-500 font-medium">Sin jugadores</p>
            <p className="text-gray-400 text-sm mt-1">
              {busqueda ? "No hay resultados para tu búsqueda" : "Crea el primer jugador del plantel"}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
            {filtrados.map((j: any) => {
              const colores = ESTADO_COLORS[j.estado] || ESTADO_COLORS.activo;
              return (
                <div key={j.id}
                  onClick={() => nav(`/jugadores/${j.id}`)}
                  className="group bg-white rounded-xl border border-gray-200 p-5 cursor-pointer hover:border-blue-300 hover:shadow-md hover:shadow-blue-500/10 transition-all text-center"
                >
                  <div className="w-12 h-12 rounded-full bg-blue-50 border-2 border-blue-100 flex items-center justify-center text-blue-700 font-bold text-lg mx-auto mb-3 group-hover:border-blue-300 transition-colors">
                    {j.nombre?.[0]}{j.apellido?.[0]}
                  </div>
                  <p className="font-semibold text-gray-900 text-sm truncate">{j.nombre} {j.apellido}</p>
                  <p className="text-xs text-gray-500 mt-0.5 mb-2">
                    {POS_SHORT[j.posicion_principal] || j.posicion_principal}
                    {j.dorsal ? ` · #${j.dorsal}` : ""}
                  </p>
                  <div className="flex items-center justify-center gap-1.5 flex-wrap">
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${colores.bg} ${colores.text}`}>
                      {j.estado}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </Layout>
  );
}
